import { useRef, type ElementType } from 'react';
import { Link, useLocation } from 'react-router';
import type { HypothesisProgramDoor, HypothesisProgramRef, HypothesisProgramReport } from '../api/useHypoteseProgrammer.ts';
import type { Hypothesis } from '../api/useHypotheses.ts';
import { periode } from '../programmer/periode.ts';
import { Ikon } from '../status/Ikon.tsx';
import { programOppforing } from '../status/status.ts';
import { hvem, stor } from './lesning.ts';
import { kortDatoAv } from './setning.ts';

/**
 * H3.4 — betaprogrammet på hypotesen (konseptsiden del A, D og E).
 *
 * Døren ligger i rommet Hypoteser, programmet i rommet Innspill. Døren er en lenke under Neste steg,
 * ikke en overgang (DD-56): den endrer ikke status. Den finnes bare for eieren i Valgt og Testes, og
 * er åpen bare når hun er behandler i hele Innspill — ellers står den låst med grunnen og navnet på
 * den som kan gi tilgangen (P3). Andre ser ingen dør og ingen setning om den (skjerm 18d).
 */

/** Rapportens konklusjon med programmets ord (`Rapport.tsx`). */
export const RAPPORT_ORD: Record<HypothesisProgramReport['conclusion'], string> = { oppfylt: 'Oppfylt', delvis: 'Delvis', ikke: 'Ikke oppfylt' };

/** DD-16: under 30 svar finnes ingen andel noe sted. Speiler serverens gulv. */
export const PROSENTGULV = 30;

const tilProgrammet = (id: string) => `/programmer?program=${id}`;

/** «Ingrid Moe kan …», «Ingrid Moe eller en annen eier kan …», eller «En eier kan …» uten navn (P3). */
export function laasTekst(d: HypothesisProgramDoor): string {
  const navn = d.accessGrantedBy?.displayName?.trim();
  const hvemKan = navn ? `${navn}${d.otherOwners ? ' eller en annen eier' : ''}` : 'En eier';
  if (d.lock === 'ingen_innspill_tilgang') return `Programmer lages i Innspill, der du ikke har tilgang. ${hvemKan} kan gi deg den.`;
  return `Programmer lages av en behandler i hele Innspill. ${hvemKan} kan endre tilgangen din.`;
}

/**
 * Døren under Neste steg (skjerm 1, 2, 18). Etter «Lukk» i skjemaet kommer hun tilbake hit med
 * fokus på døren hun brukte (skjerm 22).
 */
export function ProgramDoer({ h, door, harProgram }: { h: Hypothesis; door: HypothesisProgramDoor; harProgram: boolean }) {
  const lokasjon = useLocation();
  // Fokus én gang, når hun kommer tilbake fra skjemaet — aldri ved neste rendring.
  const fokus = useRef((lokasjon.state as { fokus?: string } | null)?.fokus === 'programdoer');
  const settFokus = (el: HTMLElement | null) => {
    if (el && fokus.current) {
      fokus.current = false;
      el.focus();
    }
  };
  if (door.canCreate) {
    return (
      <Link
        ref={settFokus}
        to={`/programmer/nytt?fra=H-${h.number}`}
        state={{ tilbake: lokasjon.pathname }}
        className="panel-knapp-lenke hb-ekstra-lenke"
        data-testid="hb-programdoer"
      >
        <Ikon navn="flask-outline" />
        {harProgram ? 'Lag et betaprogram til …' : 'Lag betaprogram fra denne …'}
      </Link>
    );
  }
  // Forklart lås: grunnen står i DOM-en ved kontrollen, aldri i et banner — det er rollen i et
  // ANNET rom som stopper én handling (presisering av «Forklarte låser», 2026-10-29).
  return (
    <span className="hb-ekstra-laast">
      <button ref={settFokus} type="button" className="panel-knapp-lenke hb-ekstra-lenke" aria-disabled="true" aria-describedby="hb-programdoer-grunn" data-testid="hb-programdoer-laast">
        <Ikon navn="lock-closed-outline" />
        Lag betaprogram fra denne
      </button>
      <span id="hb-programdoer-grunn" className="h-grunn">
        <Ikon navn="information-circle-outline" className="hb-ekstra-info" />
        <span>{laasTekst(door)}</span>
      </span>
    </span>
  );
}

/**
 * Seksjonen «Betaprogram» (skjerm 2): én rad per program med kolben, navnet og én meta-linje —
 * programmets EGEN status (et Aktiv-program endrer ikke Testes), produktet, perioden, antall med og
 * hvem som laget det. Uten Innspill (P7) har radene ikke navn, produkt eller lenke.
 */
export function BetaprogramSeksjon({ items, megId, tittelTag: Tittel, hjelp }: { items: HypothesisProgramRef[]; megId: string | undefined; tittelTag: ElementType; hjelp: boolean }) {
  if (items.length === 0) return null;
  return (
    <section className="hb-sek hb-betaprogram" aria-labelledby="hb-betaprogram-h" data-testid="hb-betaprogram">
      <div className="hb-sek-hode">
        <Tittel id="hb-betaprogram-h" className="hb-sek-tittel">
          Betaprogram
        </Tittel>
      </div>
      <ul className="hb-grunnlag-liste" aria-label="Betaprogrammene">
        {items.map((p) => (
          <ProgramRad key={p.id} p={p} megId={megId} />
        ))}
      </ul>
      {hjelp ? <p className="hb-dempet">Når rapporten er fryst, kan du bruke den som utgangspunkt for konklusjonen.</p> : null}
    </section>
  );
}

function ProgramRad({ p, megId }: { p: HypothesisProgramRef; megId: string | undefined }) {
  const o = programOppforing(p.status);
  const ledd = [
    p.productName,
    p.opensAt && p.closesAt ? periode(p.opensAt, p.closesAt) : undefined,
    p.participantCount !== undefined ? `${p.participantCount} med` : undefined,
    `${hvem(p.createdBy, megId, 'du')} laget det ${kortDatoAv(p.createdAt)}`,
  ].filter((x): x is string => Boolean(x));
  return (
    <li className="hb-grunnlag-rad" data-testid={`hb-program-${p.id}`}>
      <Ikon navn="flask-outline" className="hb-grunnlag-ikon" />
      <span className={p.name ? 'hb-grunnlag-tekst hb-fet' : 'hb-grunnlag-tekst hb-dempet'}>{p.name ?? 'Et betaprogram i Innspill'}</span>
      {p.name ? (
        <span className="hb-grunnlag-k">
          <Link to={tilProgrammet(p.id)} className="panel-knapp-lenke" aria-label={`Åpne «${p.name}» i Innspill`}>
            Åpne i Innspill
          </Link>
        </span>
      ) : null}
      <span className="hb-grunnlag-rad-meta">
        <span className={`hb-program-status hb-program-status-${p.status}`}>
          <Ikon navn={o.ikon} />
          <b>{o.etikett}</b>
        </span>
        <span>{` · ${ledd.join(' · ')}`}</span>
      </span>
    </li>
  );
}

/** Rapportens tall i én linje: «Delvis · 38 svar · 58 % fullførte alle oppgavene ± 8 · 27 med». Under 30: ingen andel (DD-16). */
export function rapportTall(r: HypothesisProgramReport): string {
  const andel =
    r.responses >= PROSENTGULV && r.completionRate != null
      ? [`${Math.round(r.completionRate * 100)} % fullførte alle oppgavene${r.completionRateLow != null && r.completionRateHigh != null ? ` ± ${Math.round(((r.completionRateHigh - r.completionRateLow) / 2) * 100)}` : ''}`]
      : [];
  const svar = r.responses < PROSENTGULV ? `${r.responses} svar, for få til andeler` : `${r.responses} svar`;
  return [RAPPORT_ORD[r.conclusion], svar, ...andel, `${r.accepted} med`].join(' · ');
}

/**
 * Raden «Utgangspunkt» på konklusjonen (skjerm 15 og 16): rapporten konklusjonen bygde på. Med
 * Innspill er rapportens navn en lenke, med det rapporten konkluderte, antall svar, produktet og
 * datoen. Uten Innspill (P7) står rapportens konklusjon og tall som en hevet flate uten lenke. Valgte
 * eieren et annet utfall enn forslaget, står det her også — beslutningstakeren ser det før han bekrefter.
 */
export function Utgangspunkt({ r, outcome }: { r: HypothesisProgramReport; outcome: string | undefined }) {
  const avviker = outcome !== undefined && outcome !== r.suggestedOutcome;
  return (
    <div className="hb-fakta-rad hb-utgangspunkt" data-testid="hb-utgangspunkt">
      <dt>Utgangspunkt</dt>
      <dd>
        {r.programName ? (
          <span className="hb-utgangspunkt-linje">
            <Ikon navn="flask-outline" className="hb-utgangspunkt-ikon" />
            <span>
              <Link to={tilProgrammet(r.programId)} className="panel-lenke">{`Programrapporten «${r.programName}»`}</Link>
              <span className="hb-utgangspunkt-meta">{` ${[RAPPORT_ORD[r.conclusion], r.responses < PROSENTGULV ? `${r.responses} svar, for få til andeler` : `${r.responses} svar`, r.productName, `fryst ${kortDatoAv(r.frozenAt)}`].filter(Boolean).join(' · ')}`}</span>
            </span>
          </span>
        ) : (
          <span className="hb-utgangspunkt-flate">
            <span className="hb-utgangspunkt-linje">
              <Ikon navn="flask-outline" className="hb-utgangspunkt-ikon" />
              <b>{`Programrapporten · fryst ${kortDatoAv(r.frozenAt)}`}</b>
            </span>
            <span>{rapportTall(r)}</span>
          </span>
        )}
        {avviker ? <span className="hb-utgangspunkt-avvik" data-testid="hb-utgangspunkt-avvik">
            <Ikon navn="create-outline" />
            {`rapporten sa ${RAPPORT_ORD[r.conclusion]}`}
          </span> : null}
      </dd>
    </div>
  );
}

/** Loggens linjer om programmet (skjerm 15): laget, og rapporten fryst. Bygd av radene, ikke av HypothesisTransition. */
export function programLoggRader(items: HypothesisProgramRef[], megId: string | undefined): Array<{ hva: string; at: string; noekkel: string }> {
  const ut: Array<{ hva: string; at: string; noekkel: string }> = [];
  for (const p of items) {
    ut.push({ hva: `${stor(hvem(p.createdBy, megId, 'Du'))} laget ${p.name ? `betaprogrammet «${p.name}»` : 'et betaprogram'}`, at: p.createdAt, noekkel: `prg-laget-${p.id}` });
    if (p.report) ut.push({ hva: 'Betaprogrammet ble avsluttet · rapporten er fryst', at: p.report.frozenAt, noekkel: `prg-fryst-${p.id}` });
  }
  return ut;
}

